import { useState } from 'react';
import { Link } from 'react-router-dom';
import { plural } from '../formato';
import { Vazio } from '../ui';

/**
 * O painel do laboratório — os gráficos e as máquinas (onda 240, RN-216).
 *
 * Desenho aprovado pelo Humberto no canvas «LabManager · Painel do
 * laboratório» (02/10/2026), no padrão Carbon Testesolo: gráfico de barras
 * com o nome do método à esquerda e o valor no fim, medidor por máquina,
 * mapa de calor máquina × dia. As contas são da 0226 (`/painel/laboratorio`);
 * aqui só se desenha.
 *
 * Desde a onda 318 (o Painel novo, spec 06b §5.2) estas peças são as de
 * «Gráficos e máquinas», que abre por baixo do Andamento: o tempo por
 * método, o «do ensaio ao laudo», as chegadas previstas, a ocupação, o mapa
 * de calor e a tabela das máquinas. Os ladrilhos de número do topo da
 * antiga «Visão geral» saíram — viraram os indicadores do Andamento e o
 * cartão «amostras recebidas sem OS» —, e o «Esperando alguém» daqui também:
 * ele repetia os cartões do Hoje.
 *
 * ── Três regras de leitura ────────────────────────────────────────────
 *
 * **Todo número responde uma pergunta escrita no próprio cartão**, e leva
 * à lista que o compõe.
 *
 * **Cor nunca decide sozinha (§18).** Toda família vem com o nome escrito;
 * «sem reserva com fila» é hachurado, não só amarelo; a parte tomada da
 * multiprensa é a ALTURA do preenchimento.
 *
 * **Nada é pintado por limiar inventado.** A meta é a primeira
 * programação (D4); o «piorou» é a comparação com o período anterior de
 * mesmo tamanho — não um número que alguém escolheu.
 */

// ── Os dados da rota ─────────────────────────────────────────────────

export interface DadosLab {
  periodo: { de: string; ate: string; ant_de: string; ant_ate: string; dias: number };
  entrada: {
    aChegar: {
      amostras: number; os: number; proximos_7: number; sem_previsao: number;
      atrasadas: number; pior_atraso: number;
    };
    chegadas: {
      dia: string; dia_semana: number; folga: boolean; feriado: boolean;
      cliente: number; testesolo: number;
    }[];
    atrasadas: {
      prevista_id: string; os_id: string; os_numero: string; cliente: string;
      identificacao_prevista: string | null; previsao_inicial: string;
      previsao: string | null; previsao_origem: string; dias_de_atraso: number;
    }[];
    semOs: { quantos: number; mais_antiga_dias: number; exemplo: string | null };
    recebidas: { periodo: number; anterior: number };
  };
  fila: {
    aguardando: number; ate_7: number; de_8_a_15: number; de_16_a_30: number;
    mais_de_30: number; entraram: number; sairam: number;
    entraram_antes: number; sairam_antes: number;
  };
  prazo: {
    porMetodo: {
      metodo_codigo: string; metodo_nome: string; escopo: string | null;
      escopo_nome: string | null; n: number;
      fila: string; execucao: string; verificacao: string; emissao: string;
      mediana: string | null; p90: string | null;
    }[];
    meta: {
      terminados: number; na_meta: number; terminados_antes: number;
      na_meta_antes: number; sem_programacao: number;
    };
    l35: {
      n: number; media: string | null; mediana: string | null; p90: string | null;
      media_antes: string | null; n_antes: number;
      porMetodo: { metodo_codigo: string; n: number; media: string }[];
    };
  };
  maquinas: {
    linhas: Maquina[];
    media: number | null; comFila: number; diasComFila: number; inaptas: number;
    dias: string[];
  };
}

interface Maquina {
  equipamento_id: string; codigo: string; nome: string;
  categoria: string; categoria_nome: string; postos: number;
  apto: boolean | null; impedimento: string | null;
  proximo_inicio: string | null;
  ocupacao: number | null; dias_com_fila: number; dias: number;
  calor: { dia: string; ocupados: number; fila: number }[];
}

// ── Pequenos ajudantes ───────────────────────────────────────────────

/** 1,5 — vírgula decimal e no máximo uma casa (padrão brasileiro, §3). */
const num = (v: string | number | null | undefined, casas = 1): string =>
  v === null || v === undefined || v === '' ? '—'
    : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: casas });

/** «05/10» de «2026-10-05», sem passar por `Date` — a data do banco já é
 *  do fuso da casa, e um `new Date('AAAA-MM-DD')` a leria em UTC (221). */
const ddmm = (iso: string | null | undefined): string =>
  iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—';

const SEMANA = ['', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];

/** A família da máquina, pela categoria — para a cor e para o nome. */
export function escopoDaMaquina(categoria: string): string {
  if (categoria.includes('adensamento')) return 'adensamento';
  if (categoria.includes('cisalhamento')) return 'cisalhamento';
  if (categoria.includes('triaxial')) return 'triaxial';
  return 'especiais';
}

/** O teto da escala de dias: o próximo múltiplo de 5 (ou de 10). */
function tetoDaEscala(maximo: number): number {
  if (maximo <= 5) return 5;
  const passo = maximo <= 40 ? 5 : 10;
  return Math.ceil(maximo / passo) * passo;
}

function Delta({ texto, tom }: { texto: string; tom: 'ok' | 'atencao' | 'critico' | 'neutro' }) {
  const cls = tom === 'ok' ? 'tag-pl ok' : tom === 'atencao' ? 'tag-pl atencao'
    : tom === 'critico' ? 'tag-pl critico' : 'tag-pl neutro';
  return <span className={cls}>{texto}</span>;
}

// ── Gráficos e máquinas ──────────────────────────────────────────────

/** O período dos gráficos, e o que a meta é (RN-216). */
export function RodapeDoPeriodo({ d }: { d: DadosLab }) {
  return (
    <p className="pl-rodape">
      Período de {ddmm(d.periodo.de)} a {ddmm(d.periodo.ate)} ({d.periodo.dias} dias corridos),
      comparado com os {d.periodo.dias} dias anteriores. A meta é a primeira programação do
      cronograma; reprogramar não a move (RN-216).
    </p>
  );
}

/** L13 + L14 — da chegada ao laudo emitido, por método, nas quatro etapas. */
export function TempoDeEntrega({ d }: { d: DadosLab }) {
  const [tabela, setTabela] = useState(false);
  const linhas = d.prazo.porMetodo;
  const teto = tetoDaEscala(Math.max(1, ...linhas.map((l) => Number(l.p90 ?? l.mediana ?? 0))));
  const marcas = [0, 1, 2, 3, 4, 5].map((i) => Math.round((teto / 5) * i));

  return (
    <section className="pl-cartao" aria-labelledby="pl-t-tempo">
      <div className="pl-cab">
        <div>
          <h2 id="pl-t-tempo">Da chegada da amostra ao laudo emitido, por método</h2>
          <p className="pl-sub">Mediana em dias corridos · traço = P90 (o caso ruim que ainda acontece)</p>
        </div>
        {linhas.length > 0 && (
          <button type="button" className="botao secundario pl-btn-p"
            onClick={() => setTabela(!tabela)}>
            {tabela ? 'Ver como gráfico' : 'Ver como tabela'}
          </button>
        )}
      </div>

      {linhas.length === 0 ? (
        <Vazio>Nenhum laudo emitido no período — o tempo de entrega aparece com o primeiro.</Vazio>
      ) : tabela ? (
        <div className="rola">
          <table className="dados pl-tabela">
            <thead>
              <tr>
                <th>Método</th><th className="num">Ensaios</th>
                <th className="num">Fila</th><th className="num">Execução</th>
                <th className="num">Verificação</th><th className="num">Emissão</th>
                <th className="num">Mediana</th><th className="num">P90</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.metodo_codigo}>
                  <td><span className="mono">{l.metodo_codigo}</span> <span className="sub">{l.metodo_nome}</span></td>
                  <td className="num">{l.n}</td>
                  <td className="num">{num(l.fila)} d</td>
                  <td className="num">{num(l.execucao)} d</td>
                  <td className="num">{num(l.verificacao)} d</td>
                  <td className="num">{num(l.emissao)} d</td>
                  <td className="num"><b>{num(l.mediana)} d</b></td>
                  <td className="num">{num(l.p90)} d</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pl-sub">As etapas são médias; a mediana e o P90, do total.</p>
        </div>
      ) : (
        <>
          <div className="pl-legenda">
            <span><i className="pl-sw k-fila" />Fila</span>
            <span><i className="pl-sw k-exec" />Execução</span>
            <span><i className="pl-sw k-verif" />Verificação</span>
            <span><i className="pl-sw k-emis" />Emissão</span>
            <span><i className="pl-sw k-p90" />P90</span>
          </div>
          <div className="pl-barras" role="img"
            aria-label={linhas.map((l) => `${l.metodo_codigo}: mediana ${num(l.mediana)} dias, P90 ${num(l.p90)} dias`).join('; ')}>
            {linhas.map((l) => {
              const partes = [Number(l.fila), Number(l.execucao), Number(l.verificacao), Number(l.emissao)];
              const soma = partes.reduce((s, x) => s + x, 0) || 1;
              const largura = (Number(l.mediana ?? 0) / teto) * 100;
              return (
                <div key={l.metodo_codigo} className="pl-barra-linha"
                  title={`${l.metodo_codigo} · ${l.n} ensaio(s) · fila ${num(l.fila)} d · execução ${num(l.execucao)} d · verificação ${num(l.verificacao)} d · emissão ${num(l.emissao)} d · P90 ${num(l.p90)} d`}>
                  <span className="pl-barra-rot mono">{l.metodo_codigo}</span>
                  <div className="pl-barra-trilho">
                    <div className="pl-barra-pilha" style={{ width: `${largura}%` }}>
                      {['k-fila', 'k-exec', 'k-verif', 'k-emis'].map((k, i) => (
                        <i key={k} className={`pl-seg ${k}`} style={{ width: `${(partes[i] / soma) * 100}%` }} />
                      ))}
                    </div>
                    {l.p90 !== null && (
                      <i className="pl-p90" style={{ left: `${(Number(l.p90) / teto) * 100}%` }} />
                    )}
                  </div>
                  <span className="pl-barra-val"><b>{num(l.mediana)} d</b> <span className="sub">· P90 {num(l.p90)} d</span></span>
                </div>
              );
            })}
          </div>
          <div className="pl-barra-linha pl-eixo mono" aria-hidden="true">
            <span className="pl-barra-rot" />
            <div className="pl-eixo-trilho">
              {marcas.map((x) => (
                <span key={x} style={{ left: `${(x / teto) * 100}%` }}>{x}</span>
              ))}
            </div>
            <span className="pl-barra-val">dias</span>
          </div>
        </>
      )}
    </section>
  );
}

/** L22 + L23 — o medidor de cada máquina especial. */
export function OcupacaoResumo({ d, aoVerMaquinas }: { d: DadosLab; aoVerMaquinas(): void }) {
  const mq = d.maquinas;
  return (
    <section className="pl-cartao" aria-labelledby="pl-t-ocup">
      <div>
        <h2 id="pl-t-ocup">Ocupação das máquinas especiais</h2>
        <p className="pl-sub">
          Dias reservados na programação ou em execução ÷ dias corridos do período (D6)
        </p>
      </div>
      {mq.linhas.length === 0 ? (
        <Vazio>Nenhuma máquina no cronograma.</Vazio>
      ) : (
        <div className="pl-medidores">
          {mq.linhas.map((l) => (
            <div key={l.equipamento_id} className={`pl-medidor escopo-${escopoDaMaquina(l.categoria)}`}>
              <div className="pl-medidor-cab">
                <span className="mono"><b>{l.codigo}</b></span>
                <span className="pl-fam">{l.categoria_nome}</span>
                <span className="pl-espaco" />
                {l.dias_com_fila > 0 && (
                  <Delta tom="atencao" texto={`▲ Sem reserva com fila · ${l.dias_com_fila} d`} />
                )}
                {l.apto === false && <Delta tom="neutro" texto="✕ Inapta hoje" />}
                <b className="pl-medidor-val">{l.ocupacao === null ? '—' : `${l.ocupacao} %`}</b>
              </div>
              <div className="pl-trilho"><i style={{ width: `${l.ocupacao ?? 0}%` }} /></div>
            </div>
          ))}
        </div>
      )}
      <div className="pl-pe">
        <span className="pl-ctx">
          Média {mq.media === null ? '—' : `${mq.media} %`} · {mq.comFila === 0
            ? 'nenhuma máquina ficou sem reserva com ensaio compatível na fila'
            : `${plural(mq.comFila, 'máquina ficou', 'máquinas ficaram')} sem reserva com ensaio compatível na fila`}
        </span>
        <button type="button" className="botao discreto" onClick={aoVerMaquinas}>
          Ver mapa de calor →
        </button>
      </div>
    </section>
  );
}

/** L35 — da conclusão do ensaio ao laudo emitido (tempo médio, sem meta). */
export function ConclusaoAoLaudo({ d }: { d: DadosLab }) {
  const l = d.prazo.l35;
  const media = l.media === null ? null : Number(l.media);
  const antes = l.media_antes === null ? null : Number(l.media_antes);
  const dif = media !== null && antes !== null ? Math.round((media - antes) * 10) / 10 : null;
  const maxMet = Math.max(1, ...l.porMetodo.map((x) => Number(x.media)));

  return (
    <section className="pl-cartao" aria-labelledby="pl-t-l35">
      <div>
        <h2 id="pl-t-l35">Da conclusão do ensaio ao laudo emitido</h2>
        <p className="pl-sub">Depois que a bancada termina, quanto tempo até virar laudo · sem meta</p>
      </div>
      {l.n === 0 ? (
        <Vazio>Nenhum laudo emitido no período.</Vazio>
      ) : (
        <>
          <div className="pl-grande">
            <span className="pl-num">{num(media)}</span>
            <span className="pl-uni">dias em média</span>
          </div>
          <div className="pl-linha">
            <span className="pl-ctx">mediana <b>{num(l.mediana)} d</b></span>
            <span className="pl-ctx">P90 <b>{num(l.p90)} d</b></span>
            {dif !== null && dif > 0 && <Delta tom="atencao" texto={`▲ ${num(dif)} d · piorou`} />}
            {dif !== null && dif < 0 && <Delta tom="ok" texto={`▼ ${num(-dif)} d · melhorou`} />}
          </div>
          <div className="pl-mini">
            {l.porMetodo.map((x) => (
              <div key={x.metodo_codigo} className="pl-mini-linha"
                title={`${x.metodo_codigo}: ${num(x.media)} dias em média, ${x.n} ensaio(s)`}>
                <span className="mono">{x.metodo_codigo}</span>
                <div className="pl-mini-trilho"><i style={{ width: `${(Number(x.media) / maxMet) * 100}%` }} /></div>
                <span className="num">{num(x.media)} d</span>
              </div>
            ))}
          </div>
          <p className="pl-sub">
            A média sobe quando poucos laudos atrasam muito: compare com a mediana.
          </p>
        </>
      )}
    </section>
  );
}

/**
 * L02 + L34 — quando a carga chega, e o que devia ter chegado.
 *
 * A rota manda a lista das que deviam ter chegado cortada em 12 (as de
 * mais atraso) e, à parte, a contagem de todas (`aChegar.atrasadas`, a
 * mesma visão). O número é a contagem: o tamanho da lista cortada diria
 * «12» com vinte atrasadas (o cuidado do Ruling R23, onda 318). A lista
 * mostra as quatro de mais atraso, e diz isso quando há mais.
 */
export function ChegadasPrevistas({ d }: { d: DadosLab }) {
  const c = d.entrada.chegadas;
  const max = Math.max(1, ...c.map((x) => x.cliente + x.testesolo));
  const atrasadas = d.entrada.atrasadas;
  const quantasAtrasadas = Math.max(atrasadas.length, Number(d.entrada.aChegar.atrasadas ?? 0));
  const mostradas = atrasadas.slice(0, 4);
  return (
    <section className="pl-cartao" aria-labelledby="pl-t-cheg">
      <div>
        <h2 id="pl-t-cheg">Chegadas previstas · próximos 10 dias</h2>
        <p className="pl-sub">
          Previsão na amostra prevista da OS · {plural(d.entrada.aChegar.sem_previsao,
            'amostra ainda sem previsão', 'amostras ainda sem previsão')}
        </p>
      </div>
      <div className="pl-legenda">
        <span><i className="pl-sw k-cli" />Previsão do cliente</span>
        <span><i className="pl-sw k-tes" />Coleta Testesolo</span>
      </div>
      <div className="pl-colunas" role="img"
        aria-label={c.map((x) => `${ddmm(x.dia)}: ${x.cliente} do cliente, ${x.testesolo} da Testesolo`).join('; ')}>
        {c.map((x) => (
          <div key={x.dia} className="pl-coluna"
            title={`${ddmm(x.dia)} · cliente ${x.cliente} · coleta Testesolo ${x.testesolo}`}>
            <span className="pl-coluna-val">{x.cliente + x.testesolo || ''}</span>
            <i className="pl-seg k-tes" style={{ height: `${(x.testesolo / max) * 100}px` }} />
            <i className="pl-seg k-cli" style={{ height: `${(x.cliente / max) * 100}px` }} />
          </div>
        ))}
      </div>
      <div className="pl-colunas-rot">
        {c.map((x) => (
          <span key={x.dia} className={x.folga ? 'folga' : undefined}>
            <span className="mono">{ddmm(x.dia)}</span>
            <br />{x.feriado ? 'feriado' : SEMANA[x.dia_semana]}
          </span>
        ))}
      </div>
      {atrasadas.length > 0 && (
        <div className="pl-atrasadas">
          <b>{plural(quantasAtrasadas, 'amostra devia ter chegado', 'amostras deviam ter chegado')}</b>
          {quantasAtrasadas > mostradas.length && ` · as ${mostradas.length} de mais atraso`}
          <ul>
            {mostradas.map((a) => (
              <li key={a.prevista_id}>
                <Link to={`/os/${a.os_id}`} className="mono">{a.os_numero}</Link>{' '}
                {a.identificacao_prevista ?? 'sem identificação'} · previsto {ddmm(a.previsao_inicial)}
                {' '}· <b>{plural(a.dias_de_atraso, 'dia', 'dias')}</b>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Link to="/recebimentos" className="pl-lnk">Ver amostras a chegar →</Link>
    </section>
  );
}

// ── Equipamentos ─────────────────────────────────────────────────────

export function EquipamentosDoPainel({ d }: { d: DadosLab }) {
  const mq = d.maquinas;
  const proximas = new Map<string, Maquina>();
  for (const l of mq.linhas) {
    if (!l.proximo_inicio) continue;
    const k = escopoDaMaquina(l.categoria);
    const atual = proximas.get(k);
    if (!atual || (atual.proximo_inicio ?? '') > l.proximo_inicio) proximas.set(k, l);
  }
  const primeira = [...proximas.values()]
    .sort((a, b) => (a.proximo_inicio ?? '').localeCompare(b.proximo_inicio ?? ''))[0];

  return (
    <div className="painel-lab">
      {/* Os quatro ladrilhos de número saíram (onda 318): o que eles diziam
          cabe numa linha, e a ocupação média já está no medidor acima. */}
      <p className="pl-ctx">
        {mq.inaptas === 0 ? 'Todas as máquinas podem entrar em ensaio hoje'
          : `${plural(mq.inaptas, 'máquina inapta', 'máquinas inaptas')} hoje (${mq.linhas
            .filter((l) => l.apto === false).map((l) => l.codigo).join(', ')})`}
        {' · '}próxima janela livre: {primeira
          ? `${ddmm(primeira.proximo_inicio)} (${primeira.codigo} · ${primeira.categoria_nome})`
          : 'nenhuma máquina apta livre'}
        {' · '}{plural(mq.diasComFila, 'máquina-dia perdido', 'máquinas-dia perdidos')} sem reserva
        com ensaio compatível na fila.{' '}
        <Link to="/cronograma">Abrir cronograma</Link> · <Link to="/equipamentos">Ver o parque</Link>
      </p>

      <section className="pl-cartao" aria-labelledby="pl-t-calor">
        <div className="pl-cab">
          <div>
            <h2 id="pl-t-calor">Máquina × dia · últimos {mq.dias.length} dias corridos</h2>
            <p className="pl-sub">
              A altura do preenchimento é a parte dos postos tomada no dia (multiprensa: vários postos)
            </p>
          </div>
          <div className="pl-legenda">
            <span><i className="pl-sw k-tomada" />Reservada / em execução</span>
            <span><i className="pl-sw k-livre" />Sem reserva</span>
            <span><i className="pl-sw k-comfila" />Sem reserva com fila</span>
          </div>
        </div>
        {mq.linhas.length === 0 ? <Vazio>Nenhuma máquina no cronograma.</Vazio> : (
          <div className="rola">
            <table className="pl-calor">
              <thead>
                <tr>
                  <th scope="col">Máquina</th>
                  {mq.dias.map((dia) => <th key={dia} scope="col" className="mono">{dia.slice(8, 10)}</th>)}
                  <th scope="col" className="num">Ocup.</th>
                </tr>
              </thead>
              <tbody>
                {mq.linhas.map((l) => (
                  <tr key={l.equipamento_id} className={`escopo-${escopoDaMaquina(l.categoria)}`}>
                    <th scope="row">
                      <span className="mono"><b>{l.codigo}</b></span>
                      <span className="pl-fam">{l.categoria_nome} · {plural(l.postos, 'posto', 'postos')}</span>
                    </th>
                    {l.calor.map((c) => {
                      const tomados = Math.min(c.ocupados, l.postos);
                      const comFila = tomados < l.postos && c.fila > 0;
                      const titulo = `${l.codigo} · ${ddmm(c.dia)} · ${tomados} de ${plural(l.postos, 'posto tomado', 'postos tomados')}`
                        + (comFila ? ` · ${plural(c.fila, 'ensaio compatível', 'ensaios compatíveis')} esperando` : '');
                      return (
                        <td key={c.dia} title={titulo} className={comFila ? 'k-comfila' : undefined}>
                          <i style={{ height: `${(tomados / l.postos) * 100}%` }} />
                          <span className="so-leitor">{titulo}</span>
                        </td>
                      );
                    })}
                    <td className="num"><b>{l.ocupacao === null ? '—' : `${l.ocupacao} %`}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="pl-cartao" aria-labelledby="pl-t-tabm">
        <h2 id="pl-t-tabm">Máquinas especiais</h2>
        <div className="rola">
          <table className="dados pl-tabela">
            <thead>
              <tr>
                <th>Máquina</th><th>Família</th><th className="num">Postos</th>
                <th className="num">Ocupação</th><th className="num">Sem reserva com fila</th>
                <th>Hoje</th><th>Próxima janela livre</th>
              </tr>
            </thead>
            <tbody>
              {mq.linhas.map((l) => (
                <tr key={l.equipamento_id}>
                  <td><Link to={`/equipamento/${l.equipamento_id}`} className="mono">{l.codigo}</Link> <span className="sub">{l.nome}</span></td>
                  <td>{l.categoria_nome}</td>
                  <td className="num">{l.postos}</td>
                  <td className="num"><b>{l.ocupacao === null ? '—' : `${l.ocupacao} %`}</b></td>
                  <td className="num">{l.dias_com_fila > 0 ? `${l.dias_com_fila} d` : <span className="nada">—</span>}</td>
                  <td>{l.apto === false
                    ? <span className="selo critico" title={l.impedimento ?? undefined}>Inapta</span>
                    : <span className="selo ok">Apta</span>}</td>
                  <td className="mono">{ddmm(l.proximo_inicio)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="pl-sub">
          A ocupação divide pelos dias corridos do período; a inaptidão é a de hoje — o
          histórico de indisponibilidade por motivo entra numa próxima onda.
        </p>
      </section>
    </div>
  );
}
